import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import getTheme from "@Components/theme/base";

type ColorMode = "light" | "dark";

interface ColorModeContextValue {
  /** Light/dark of the selected theme — what the sections branch on. */
  mode: ColorMode;
  /** Selected app theme (a key of components/theme/base getTheme). */
  themeName: string;
  setThemeName: (name: string) => void;
  /** Flips between the Fidava dark default and the Fidava light theme. */
  toggle: () => void;
}

// The site offers the same theme catalogue as the app (ThemePickerPanel);
// Fidava Dark is the site's default look.
export const SITE_DEFAULT_THEME = "defaultDark";
const SITE_LIGHT_THEME = "defaultLight";

// One theme for the app and the site: the app's own keys. `aem-theme` is the
// theme (ThemePicker / themeProviderStore), `converge-theme-mode` its
// light/dark mirror that the pre-React splash in Index.cshtml reads.
const THEME_KEY = "aem-theme";
const MODE_KEY = "converge-theme-mode";
// Keys the site used before it shared the app's — migrated once, then removed.
const LEGACY_SITE_KEYS = ["converge-site-theme", "converge-site-theme-mode", "converge-site-color-mode"];

const modeOf = (name: string): ColorMode =>
  getTheme(name)?.palette?.mode === "light" ? "light" : "dark";

const resolveInitial = (): string => {
  if (typeof window === "undefined") return SITE_DEFAULT_THEME;
  try {
    const legacy = window.localStorage.getItem("converge-site-theme");
    LEGACY_SITE_KEYS.forEach((key) => window.localStorage.removeItem(key));
    const stored = window.localStorage.getItem(THEME_KEY);
    if (stored && getTheme(stored)) return stored;
    // A theme picked on the site before the keys were shared.
    if (legacy && getTheme(legacy)) {
      window.localStorage.setItem(THEME_KEY, legacy);
      window.localStorage.setItem(MODE_KEY, modeOf(legacy));
      return legacy;
    }
  } catch {
    /* localStorage may be blocked in private mode — fall through */
  }
  // Nothing chosen yet: Fidava Dark on the site. Not written back, so a first
  // visit doesn't pick the visitor's app theme for them.
  return SITE_DEFAULT_THEME;
};

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: modeOf(SITE_DEFAULT_THEME),
  themeName: SITE_DEFAULT_THEME,
  setThemeName: () => undefined,
  toggle: () => undefined
});

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeName, setThemeNameState] = useState<string>(resolveInitial);
  const mode = modeOf(themeName);

  // An explicit choice is stored in the app's keys, so the app opens in it too.
  const choose = useCallback((name: string) => {
    if (!getTheme(name)) return;
    setThemeNameState(name);
    try {
      window.localStorage.setItem(THEME_KEY, name);
      window.localStorage.setItem(MODE_KEY, modeOf(name));
    } catch {
      /* private mode — accept that the choice is session-only */
    }
  }, []);

  const toggle = useCallback(() => {
    choose(modeOf(themeName) === "dark" ? SITE_LIGHT_THEME : SITE_DEFAULT_THEME);
  }, [choose, themeName]);

  const value = useMemo(() => ({ mode, themeName, setThemeName: choose, toggle }), [mode, themeName, choose, toggle]);
  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
};

export const useColorMode = (): ColorModeContextValue => useContext(ColorModeContext);
